import React, {
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  DragEvent,
  memo,
} from 'react';
import useApplyVisitNote from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/VisitNoteSalt/useApplyVisitNote';
import {
  useFormContext,
  useWatch,
} from 'react-hook-form';
import Divider from '@mui/material/Divider';
import {
  useNavigate,
} from 'react-router-dom';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import AddIcon from '@mui/icons-material/Add';
import {
  MultimediaType,
} from 'pages/Dashboard/utils/constants';
import {
  PatientForm,
} from 'pages/Dashboard/types/patient';
import Button from 'pages/Dashboard/components/Button';
import {
  useUploadBulkMultimedia,
} from 'pages/Dashboard/pages/Charts/pages/Details/hooks';
import {
  useGetAllowedFileTypes,
} from 'pages/Dashboard/hooks/media';
import {
  mediaEventChannel,
  patientEventChannel,
  visitNoteEventChannel,
  VisitNoteParams,
} from 'pages/Dashboard/services/eventChannels';
import {
  SECTIONS,
  VisitNote,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/types/visitNote';
import {
  MultiMediaResponse,
  VisitNoteDTO,
} from 'dtos';
import ERxButton from 'pages/Dashboard/pages/Charts/pages/Details/components/ERx';
import HPI from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MainContent/HPI';
import type {
  FileLocalUrl,
} from 'pages/Dashboard/components/FilePicker';
import {
  useMixpanelTrack,
  useNotify,
  useUserSettings,
} from 'core/hooks';
import {
  useUpsertUserSettings,
} from 'pages/Dashboard/hooks/useUserSettings';
import MediaPreview from 'pages/Dashboard/components/MediaPreview';
import clsx from 'clsx';
import MediaUploadDropzone from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MainContent/MediaUploadDropzone';
import {
  useGetFavoriteCodes,
} from 'views/EMR/Settings/hooks';
import defer from 'lodash/defer';
import {
  useSnapActions,
  useSnapGeneratedNote,
  useSnapIsGeneratingNote,
  useSnapVisitDetails,
} from 'views/EMR/stores/snap';
import {
  useSectionRefs,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/hooks';
import {
  generateDropzoneAccept,
} from 'core/mimeMap';
import useGetPatientMediaByMultimediaType from 'pages/Dashboard/hooks/useGetPatientMediaByMultimediaType';
import PhysicalExam from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MainContent/PhysicalExam';
import RadiologySection from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MainContent/Radiology';
import AdditionalAssessmentSection from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MainContent/AdditionalAssessment';
import PlanSection from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MainContent/Plan';
import AssessmentCodesSection from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MainContent/AssessmentCodes';
import ServiceCodesSection from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MainContent/ServiceCodes';
import CustomServiceCodesSection from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MainContent/CustomServiceCodes';
import {
  getPatientChartPath,
  getValidChartTab,
} from 'pages/Dashboard/pages/Charts/utils';
import {
  useAppendToVisitNote,
} from 'pages/Dashboard/pages/Billing/hooks';
import {
 datadogLogs,
} from '@datadog/browser-logs';

export {
  fieldChangedCSS,
  visitNoteEditorActions,
  visitNoteAdditionalAssessmentActions,
  visitNoteBillingActions,
  visitNoteCustomServiceActions,
  assessmentLookupClasses,
  billingLookupClasses,
  customServiceLookupClasses,
  serviceCodesFormKeys,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MainContent/constants';

type VisitNoteFormFields = 'physicalExam'
  | 'encounterId'
  | 'hpi'
  | 'radiology'
  | 'additionalHPI'
  | 'plan'
  | 'additionalAssessment'
  | 'isFinalized'
  | 'updatedOn'
  | 'visitNoteId';

export type VisitNoteData = Pick<
  VisitNoteDTO,
  VisitNoteFormFields | 'version'
> & Pick<VisitNote, 'bill'>;

export type Props = {
  encounterId: number;
  visitNote: VisitNote;
  editable: boolean;
  savingVisitNote: boolean;
  onSubmit: (data: VisitNoteData) => Promise<void>;
};

function MainContent({
  visitNote: _visitNote,
  encounterId,
  editable,
  savingVisitNote,
  onSubmit,
}: Props) {
  const {
    hpi,
    patient,
    patientId = 0,
  } = _visitNote;

  const contentRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useSectionRefs();
  const navigate = useNavigate();
  const { mutateAsync: uploadBulk } = useUploadBulkMultimedia();
  const {
    data: visitNoteImages = [],
    refetch: refetchVisitNoteImages,
  } = useGetPatientMediaByMultimediaType(
    patientId,
    [MultimediaType.VisitNoteImage],
  );
  const [showRadiology, setShowRadioLogy] = useState<boolean>(false);
  const [
    showAdditionalAssessment,
    setShowAdditionalAssessment,
  ] = useState<boolean>(false);
  const notify = useNotify();

  const { settings } = useUserSettings();
  const { mutateAsync: updateSettings } = useUpsertUserSettings();
  const {
    control,
    handleSubmit,
  } = useFormContext<VisitNoteData>();
  const saveVisitNote = useCallback(
    () => handleSubmit(onSubmit)(),
    [handleSubmit, onSubmit],
  );

  const additionalHPI = useWatch({
    control,
    name: 'additionalHPI',
  });
  const radiology = useWatch({
    control,
    name: 'radiology',
  });
  const additionalAssessment = useWatch({
    control,
    name: 'additionalAssessment',
  });
  const currentVisitNoteId = useWatch({
    control,
    name: 'visitNoteId',
  }) ?? 0;

  const hasAdditionalAssessment = useMemo(() => (
    showAdditionalAssessment
    || !isNil(additionalAssessment)
    || settings?.showAdditionalAssessment
  ), [
    showAdditionalAssessment,
    additionalAssessment,
    settings?.showAdditionalAssessment,
  ]);

  const showHPI = useMemo(() => (
    editable || !isNil(additionalHPI) || hpi?.hpiFromIntakeElements?.length > 0
  ), [editable, additionalHPI, hpi?.hpiFromIntakeElements?.length]);

  const handleImageUpload = async (data: FileLocalUrl[]) => {
    await uploadBulk({
      patientId,
      mediaList: data.map((file) => ({
        multimediaType: MultimediaType.VisitNoteImage,
        dataUrl: file?.dataUrl ?? '',
        fileName: file?.file?.name ?? '',
      })),
    });
    notify({
      variant: 'success',
      message: 'Media uploaded successfully!',
    });
    await refetchVisitNoteImages();
  };

  const openChartTab = ({
    patientId,
    tab,
  }: VisitNoteParams) => {
    const nextTab = getValidChartTab(typeof tab === 'string' ? tab : undefined);

    if (nextTab === 'medications-and-allergies') {
      patientEventChannel.emit('onEditMedicalBackground', {
        patient,
        id: 'medications',
      });
    } else if (patientId > 0) {
      navigate(getPatientChartPath(patientId, nextTab) ?? '', { replace: true });
    }
  };

  const onShowAdditionalAssessmentClick = async () => {
    setShowAdditionalAssessment(true);
    const preferenceJson = JSON.stringify({
      ...settings,
      showAdditionalAssessment: true,
    });
    await updateSettings({ preferenceJson });
  };

  const scrollToBottom = () => {
    const visitNoteContainer = contentRef.current;
    setTimeout(() => {
      if (!isNil(visitNoteContainer)) {
        visitNoteContainer.scrollTop = visitNoteContainer.scrollHeight;
      }
    }, 0);
  };

  useEffect(() => {
    const unsubscribeFromMediaEventChannel = mediaEventChannel.on('onMediaDeleted', async () => {
      await refetchVisitNoteImages();
    });

    const unsubscribeFromServiceCodesUpdateChannel = visitNoteEventChannel.on('onServiceCodesUpdate', (type) => {
      if (type === 'CustomCode') {
        scrollToBottom();
      }
    });

    return () => {
      unsubscribeFromMediaEventChannel();
      unsubscribeFromServiceCodesUpdateChannel();
    };
  }, []);

  const {
    data: favoriteCodes,
    refetch: refetchFavoriteCode,
  } = useGetFavoriteCodes();

  const allowedFileTypes = useGetAllowedFileTypes();

  const acceptedFileTypes = useMemo(() => {
    const cleanExtensions = allowedFileTypes.split(',')
      .map((ext) => ext.trim().replace(/^\./, ''));
    return generateDropzoneAccept(cleanExtensions);
  }, [allowedFileTypes]);

  const visitDetails = useSnapVisitDetails();
  const generatedNote = useSnapGeneratedNote();
  const {
    onNavigateToVisitNote,
    onNavigateAwayFromVisitNote,
    closeSnap,
  } = useSnapActions();

  const mixpanelTrack = useMixpanelTrack();
  const [isUploading, setIsUploading] = useState<boolean>(false);

  useLayoutEffect(() => {
    onNavigateToVisitNote(encounterId);

    return () => {
      const showRedirectMessage = onNavigateAwayFromVisitNote();
      if (showRedirectMessage) {
        notify({
          message: 'Snap running in background — controls at bottom-right.',
          variant: 'info',
        });
        mixpanelTrack('snap-navigated-away-from-visit-note');
      }
    };
  }, []);

  const roboscribeInProgress = useSnapIsGeneratingNote();

  const {
    appendTextFieldWithDoubleLine,
  } = useAppendToVisitNote();

  useEffect(() => {
    if (!isNil(generatedNote) && visitDetails?.encounterId === encounterId) {
      defer(() => {
        datadogLogs.logger.info(`[Snap] Setting generated note in visit note`);
        generatedNote?.forEach((section) => {
          const fieldName = SECTIONS[section.key];
          appendTextFieldWithDoubleLine(fieldName, section.text);
        });
        notify({
          message: 'Note generated successfully',
          variant: 'success',
        });
        closeSnap();
        mixpanelTrack('snap-generated-note-in-visit-note');
      });
    }
  }, [generatedNote]);

  const handleMediaUpload = useCallback(async (filesWithUrl: FileLocalUrl[]) => {
    if (filesWithUrl.length === 0) {
      notify({ message: 'Invalid file, cannot be uploaded.', variant: 'error' });
    } else {
      try {
        setIsUploading(true);
        await handleImageUpload(filesWithUrl);
      } catch (e) {
        notify({ message: 'Failed to upload the file.', variant: 'error' });
      } finally {
        setIsUploading(false);
      }
    }
  }, [notify, handleImageUpload]);

  useApplyVisitNote(patientId);

  const blockEditorDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'none';
  }, []);

  return (
    <div
      className="flex-1 w-full flex overflow-auto px-4"
      ref={contentRef as RefObject<HTMLDivElement>}
    >
      <div className="flex-1 flex flex-col gap-2 h-full w-full mt-4">
        {showHPI && (
        <HPI
          editable={editable && !roboscribeInProgress}
          sectionRef={sectionRefs.additionalHPI}
          onDragOver={blockEditorDrop}
        />
        )}
        <div className={clsx('flex flex-row w-full items-center flex-wrap gap-2', {
          '!mt-0': !editable && visitNoteImages.length === 0,
        })}
        >
          {visitNoteImages.map((media: MultiMediaResponse) => (
            <MediaPreview
              key={media.mediaId}
              media={media}
              className="!h-[72px] !w-[72px] object-cover border border-gray-200 hover:border-gray-300 rounded"
              thumbnail
            />
          ))}
          {editable && (
          <div className="flex items-center gap-2">
            <MediaUploadDropzone
              onUpload={handleMediaUpload}
              accept={acceptedFileTypes}
              isUploading={isUploading}
              disabled={isUploading || savingVisitNote}
            />
          </div>
          )}
        </div>
        <PhysicalExam
          editable={editable && !roboscribeInProgress}
          sectionRef={sectionRefs.physicalExam}
        />
        {editable && !showRadiology && !radiology && (
          <Button
            className="!font-medium"
            color="primary"
            type="button"
            variant="text"
            disabled={savingVisitNote}
            onClick={() => { setShowRadioLogy(true); }}
          >
            <AddIcon
              className="mr-1"
              fontSize="small"
            />
            Add Review of Imaging
          </Button>
        )}
        {(showRadiology || !!radiology) && (
          <RadiologySection
            editable={editable}
            sectionRef={sectionRefs.radiology}
          />
        )}
        <AssessmentCodesSection
          patientId={patientId}
          visitNoteId={currentVisitNoteId}
          saveVisitNote={saveVisitNote}
          sectionRef={sectionRefs.assessmentCodes}
          favorites={favoriteCodes?.favoritedAssessmentCodes ?? []}
          refetchFavoriteCode={refetchFavoriteCode}
        />
        {hasAdditionalAssessment && (
          <AdditionalAssessmentSection
            editable={editable}
            disabled={!editable || roboscribeInProgress}
            sectionRef={sectionRefs.additionalAssessment}
          />
        )}
        {editable && !hasAdditionalAssessment && (
          <Button
            className="!font-medium"
            color="primary"
            type="button"
            variant="text"
            disabled={savingVisitNote}
            onClick={onShowAdditionalAssessmentClick}
          >
            <AddIcon
              className="mr-1"
              fontSize="small"
            />
            Add Additional Assessment
          </Button>
        )}
        {editable && (
          <ERxButton
            className="!mt-2"
            patient={omit(patient, ['insurance', 'socialHistory']) as PatientForm}
            onClick={() => {
              openChartTab({
                patientId,
                encounterId,
                tab: isNil(patient?.dosespotError)
                  ? 'medications-and-allergies'
                  : 'demographics',
              });
            }}
          />
        )}
        <PlanSection
          editable={editable}
          disabled={!editable || roboscribeInProgress}
          sectionRef={sectionRefs.plan}
        />
        <Divider
          flexItem
          className="!my-4"
        />
        <div className="flex gap-4 w-100 justify-between">
          <h2 className="text-lg font-semibold">Billing</h2>
        </div>
        <ServiceCodesSection
          patientId={patientId}
          visitNoteId={currentVisitNoteId}
          saveVisitNote={saveVisitNote}
          sectionRef={sectionRefs.serviceCodes}
          favorites={favoriteCodes?.favoritedBillingCodes ?? []}
          refetchFavoriteCode={refetchFavoriteCode}
        />
        <CustomServiceCodesSection
          patientId={patientId}
          visitNoteId={currentVisitNoteId}
          sectionRef={sectionRefs.customServiceCodes}
          favorites={favoriteCodes?.favoriteCustomServiceCodes ?? []}
          refetchFavoriteCode={refetchFavoriteCode}
        />
      </div>
    </div>
  );
}

export default memo(MainContent);
